import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Smartphone, Laptop, Trash2, LogOut, WifiOff, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/common";

/* Spec §16 — Devices dashboard: list every device with cached offline packs +
   remote sign-out + remove-offline actions. */

function iconFor(label) {
  const l = (label || "").toLowerCase();
  if (l.includes("iphone") || l.includes("android")) return Smartphone;
  return Laptop;
}

function relTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function DevicesDashboard() {
  const { user } = useAuth() || {};
  const [items, setItems] = useState(null);

  const load = () => api.get("/devices/").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { if (user) load(); }, [user]);

  const signOut = async (d) => {
    if (!window.confirm(`Sign out ${d.device_label}? They'll be signed out on next sync.`)) return;
    try { await api.post(`/devices/${d.device_id}/sign-out`); toast.success("Signed out"); load(); }
    catch { toast.error("Failed"); }
  };

  const removeOffline = async (d) => {
    if (!window.confirm(`Remove all offline packs from ${d.device_label}?`)) return;
    try { await api.post(`/devices/${d.device_id}/remove-offline`); toast.success("Offline packs cleared"); load(); }
    catch { toast.error("Failed"); }
  };

  if (!user) return <div className="max-w-lg mx-auto p-8 text-center"><div className="font-serif font-800 text-2xl">Sign in to see your devices</div></div>;

  return (
    <div className="max-w-4xl mx-auto px-5 py-8" data-testid="devices-page">
      <Link to="/travel-hub" className="inline-flex items-center gap-1 text-blue-700 text-sm hover:underline mb-3">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <h1 className="font-serif font-800 text-4xl text-slate-900">Your devices</h1>
      <p className="text-slate-600 mt-2 max-w-2xl">Every phone, tablet or computer that has synced an offline trip. Remote sign-out and clearing offline packs takes effect on the device's next sync.</p>

      <div className="mt-8">
        {items === null ? <Spinner /> : items.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-slate-200 p-10 text-center">
            <Smartphone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <div className="font-serif font-800 text-xl text-slate-800">No devices yet</div>
            <div className="text-sm text-slate-500 mt-1">Download an offline trip pack from any device — it'll appear here.</div>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((d) => {
              const Icon = iconFor(d.device_label);
              return (
                <li key={d.device_id} data-testid={`device-${d.device_id}`}
                  className={`rounded-2xl border p-4 flex items-center gap-4 shadow-sm ${d.signed_out ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`}>
                  <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-700 grid place-items-center shrink-0"><Icon className="w-5 h-5" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-serif font-800 text-lg text-slate-900 truncate">
                      {d.device_label} {d.signed_out && <span className="text-[10px] font-800 uppercase tracking-wider bg-slate-200 text-slate-700 rounded-full px-1.5 py-0.5 ml-1">Signed out</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {relTime(d.last_active_at)}</span>
                      {d.offline_trip_count > 0 && <span className="inline-flex items-center gap-1"><WifiOff className="w-3 h-3" /> {d.offline_trip_count} offline trip{d.offline_trip_count === 1 ? "" : "s"}</span>}
                    </div>
                  </div>
                  {d.offline_trip_count > 0 && (
                    <button onClick={() => removeOffline(d)} title="Remove offline packs"
                      data-testid={`device-remove-offline-${d.device_id}`}
                      className="w-9 h-9 rounded-full grid place-items-center text-amber-700 hover:bg-amber-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  {!d.signed_out && (
                    <button onClick={() => signOut(d)} title="Sign out remotely"
                      data-testid={`device-signout-${d.device_id}`}
                      className="w-9 h-9 rounded-full grid place-items-center text-rose-600 hover:bg-rose-50">
                      <LogOut className="w-4 h-4" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
